import { getUserReferenceName } from '../../utils/Support/supportHelpers';
import { ISupportTicket } from './ISupportTicket';

interface SubstitutionContext {
	ticket: ISupportTicket;
	agentName: string;
}

/** A saved-reply merge variable: the `{token}` inserted into a reply plus its display label. */
export interface SavedReplyVariable {
	/** Bare variable name matched by the resolver in {@link substituteVariables}. */
	name: string;
	/** The `{...}` token inserted into the reply body. */
	token: string;
	/** Human-friendly label for the settings "Insert variable" menu. */
	label: string;
}

/**
 * Single source of truth for the merge variables offered in the saved-reply editor and
 * resolved by {@link substituteVariables}. Keep in lockstep with the resolver switch below —
 * the model spec round-trips every entry to catch drift.
 */
export const SAVED_REPLY_VARIABLES: readonly SavedReplyVariable[] = [
	{ name: 'customer_name', token: '{customer_name}', label: 'Customer name' },
	{ name: 'customer_first_name', token: '{customer_first_name}', label: 'Customer first name' },
	{ name: 'customer_email', token: '{customer_email}', label: 'Customer email' },
	{ name: 'account_name', token: '{account_name}', label: 'Account name' },
	{ name: 'csm_name', token: '{csm_name}', label: 'CSM name' },
	{ name: 'csm_email', token: '{csm_email}', label: 'CSM email' },
	{ name: 'agent_name', token: '{agent_name}', label: 'Agent name' },
	{ name: 'ticket_number', token: '{ticket_number}', label: 'Ticket number' },
];

const VARIABLE_REGEX = /\{(\w+)(?:\|([^}]*))?\}/g;

export function substituteVariables(template: string, context: SubstitutionContext): string {
	return template.replace(VARIABLE_REGEX, (match, variable, fallback) => {
		const value = resolveVariable(variable, context);
		if (value) return value;
		if (fallback !== undefined) return fallback;
		return match; // leave unresolved tokens as-is
	});
}

function resolveVariable(variable: string, context: SubstitutionContext): string | null {
	const { ticket, agentName } = context;
	switch (variable) {
		case 'customer_name':
			return ticket.contactName || null;
		case 'customer_first_name':
			return ticket.contactName?.split(' ')[0] || null;
		case 'customer_email':
			return ticket.contactEmail || null;
		case 'account_name':
			return ticket.accountName || null;
		case 'csm_name':
			return getUserReferenceName(ticket.csm) || null;
		case 'csm_email':
			return ticket.csm?.email || null;
		case 'agent_name':
			return agentName || null;
		case 'ticket_number':
			return ticket.number ? String(ticket.number) : null;
		default:
			return null;
	}
}

export type { SubstitutionContext };
